import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Clock3, BadgeCheck, Building2, ArrowUpRight } from 'lucide-react';
import { AnimatedCounter } from './DashboardCharts';

const metrics = [
  { key: 'total', label: 'Assessments', helper: 'All records', icon: FileText, accent: 'emerald' },
  { key: 'active', label: 'In progress', helper: 'Requires action', icon: Clock3, accent: 'amber' },
  { key: 'completed', label: 'Completed', helper: 'Ready to review', icon: BadgeCheck, accent: 'green' },
  { key: 'organizations', label: 'Organizations', helper: 'In your workspace', icon: Building2, accent: 'sky' },
];

const accentClasses = {
  emerald: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300',
  amber: 'border-amber-400/25 bg-amber-400/10 text-amber-300',
  green: 'border-green-400/25 bg-green-400/10 text-green-300',
  sky: 'border-sky-400/25 bg-sky-400/10 text-sky-300',
};

const StatsGrid = ({ stats }) => {
  const navigate = useNavigate();
  const values = {
    total: stats?.total_assessments || 0,
    active: stats?.in_progress_assessments || 0,
    completed: stats?.completed_assessments || 0,
    organizations: stats?.total_organizations || 0,
  };
  const routes = {
    total: '/admin?tab=assessments',
    active: '/admin?tab=assessments&filter=in_progress',
    completed: '/admin?tab=assessments&filter=completed',
    organizations: '/organizations',
  };

  return (
    <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-tour="stats-grid" data-testid="dashboard-stats-grid">
      {metrics.map((metric, index) => {
        const Icon = metric.icon;
        return (
          <motion.button
            key={metric.key}
            type="button"
            className="dashboard-glass-interactive group rounded-xl p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 + index * 0.06 }}
            onClick={() => navigate(routes[metric.key])}
            data-testid={`dashboard-stat-${metric.key}`}
          >
            <div className="flex items-start justify-between">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg border ${accentClasses[metric.accent]}`}>
                <Icon className="h-5 w-5" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-600 transition-colors group-hover:text-slate-300" />
            </div>
            <div className="mt-6 flex items-end justify-between gap-3">
              <div>
                <p className="font-heading text-3xl font-semibold text-white" data-testid={`dashboard-stat-${metric.key}-value`}><AnimatedCounter value={values[metric.key]} /></p>
                <p className="mt-1 text-sm font-medium text-slate-200">{metric.label}</p>
              </div>
              <p className="pb-0.5 text-right text-[11px] text-slate-500">{metric.helper}</p>
            </div>
          </motion.button>
        );
      })}
    </section>
  );
};

export default StatsGrid;